import { useState, useRef } from 'react'
import { Upload, Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '../../../shared'
import { parsePayslip, type ParsedPayslip } from '../services/payslipParser'
import PayslipConfirmModal from './PayslipConfirmModal'

interface PayslipUploadProps {
  onParsed: (data: ParsedPayslip) => void
  onError: (msg: string) => void
}

type Status = 'idle' | 'loading' | 'success' | 'error'

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.webp'

export default function PayslipUpload({ onParsed, onError }: PayslipUploadProps) {
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [parsedData, setParsedData] = useState<ParsedPayslip | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const processFile = async (file: File) => {
    setStatus('loading')
    setErrorMsg('')
    try {
      const result = await parsePayslip(file)
      setStatus('success')
      setParsedData(result)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler'
      setStatus('error')
      setErrorMsg(msg)
      onError(msg)
    }
  }

  const handleFile = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    processFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleFile(e.dataTransfer.files)
  }

  return (
    <div className="space-y-3">
      {/* Upload area */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
          dragOver
            ? "border-blue-400 bg-blue-50 dark:bg-blue-950/30"
            : "border-zinc-300 dark:border-zinc-600 hover:border-blue-400 dark:hover:border-blue-500",
          status === 'loading' && "pointer-events-none opacity-60"
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => handleFile(e.target.files)}
        />

        {status === 'loading' ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <span className="text-sm text-zinc-600 dark:text-zinc-400">Wird analysiert...</span>
          </div>
        ) : status === 'success' ? (
          <div className="flex flex-col items-center gap-2">
            <CheckCircle className="h-8 w-8 text-green" />
            <span className="text-sm text-green dark:text-green">Felder wurden automatisch ausgefüllt</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-zinc-400" />
            <span className="text-sm text-zinc-600 dark:text-zinc-400">
              Gehaltsabrechnung hochladen (PDF oder Bild)
            </span>
            <span className="text-xs text-zinc-400">Klicken oder per Drag & Drop</span>
          </div>
        )}
      </div>

      {/* Error */}
      {status === 'error' && errorMsg && (
        <div className="flex items-center gap-2 text-sm text-red dark:text-red">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Privacy notice */}
      <div className="flex items-start gap-2 text-xs text-zinc-500 dark:text-zinc-400">
        <Shield className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          Deine Gehaltsabrechnung wird nur lokal verarbeitet und niemals gespeichert.
          Die KI-Analyse erfolgt direkt in deinem Browser.
        </span>
      </div>

      {/* Confirmation modal */}
      {parsedData && (
        <PayslipConfirmModal
          data={parsedData}
          onConfirm={(confirmed) => {
            onParsed(confirmed)
            setParsedData(null)
            setTimeout(() => setStatus('idle'), 3000)
          }}
          onCancel={() => {
            setParsedData(null)
            setStatus('idle')
          }}
        />
      )}
    </div>
  )
}
